// src/pages/SellerProperties.jsx
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "./PropertyContext";
import AddPropertyPopup from "./AddPropertyPopup";
import DeletePropertyModal from "../../components/DeletePropertyModal/DeletePropertyModal";
import { API_BASE_URL } from "../../config/api.config";
import { authAPI, sellerDashboardAPI, sellerProfileAPI, sellerPropertiesAPI } from "../../services/api.service";
import "../styles/SellerProperties.css";

// No property limit - sellers can list multiple properties
// Each property requires its own ₹100/month subscription
const FREE_LISTING_SLOTS = 1;
const FREE_PLAN_LABEL = 'Free (1 month)';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const sortPropertiesByCreatedAt = (props) =>
  [...(props || [])].sort(
    (a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at)
  );

const getPaidSlotCount = (planInfo) =>
  Number(planInfo?.paid_slots ?? 0);

/** Free trial applies only to listings beyond paid subscription slots. */
const getFreeListingPropertyId = (props, paidSlots = 0) => {
  if (!props?.length) return null;

  const sorted = sortPropertiesByCreatedAt(props);
  if (paidSlots > 0) {
    if (sorted.length <= paidSlots) return null;
    return sorted[paidSlots]?.id ?? null;
  }

  return sorted[0]?.id ?? null;
};

const formatDaysLeftLabel = (daysLeft, isExpired = false) => {
  if (isExpired || daysLeft <= 0) return 'Expired';
  return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
};

const toDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

  // Handle unix timestamps (seconds or milliseconds) as numbers/number-strings.
  if (typeof raw === "number" || (typeof raw === "string" && raw.trim() !== "" && raw.trim().match(/^\d+$/))) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    // If it's 10 digits-ish -> seconds, otherwise milliseconds.
    return new Date(n < 1e12 ? n * 1000 : n);
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getRemainingDays = (endDateRaw) => {
  const endDate = toDate(endDateRaw);
  if (!endDate) return 0;

  const diffMs = endDate.getTime() - Date.now();
  if (diffMs <= 0) return 0;

  // Keep "1 day left" while there is any active time remaining.
  return Math.max(1, Math.ceil(diffMs / DAY_IN_MS));
};

const hasActiveFreeTrial = (subscription, user) => {
  const source = String(subscription?.source || '').toLowerCase();
  const expiryRaw = subscription?.end_date || subscription?.nearest_expiry;
  const expiryDate = toDate(expiryRaw);
  const isSubscriptionTrialActive =
    expiryDate &&
    expiryDate.getTime() > Date.now() &&
    source !== 'paid_plan';

  if (isSubscriptionTrialActive) {
    return true;
  }

  const createdAtRaw = user?.created_at || user?.createdAt;
  if (!createdAtRaw) {
    return false;
  }

  const createdAt = toDate(createdAtRaw);
  if (!createdAt) {
    return false;
  }

  const trialEndsAt = createdAt.getTime() + (30 * 24 * 60 * 60 * 1000);
  return Date.now() < trialEndsAt;
};

const SellerProperties = () => {
  const navigate = useNavigate();
  const { properties, deleteProperty, refreshProperties, setProperties } = useProperty();
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [propertyToToggle, setPropertyToToggle] = useState(null);
  // const [planInfo, setPlanInfo] = useState({ total_slots: 0, active_properties: 0, remaining_slots: 0, nearest_expiry: null });
  const [planInfo, setPlanInfo] = useState({ 
  total_slots: 0, 
  active_properties: 0, 
  remaining_slots: 0, 
  nearest_expiry: null,
  active_subscriptions: [],
  paid_slots: 0,
  has_free_trial: false,
  trial_created_at: null
});
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [togglingPropertyId, setTogglingPropertyId] = useState(null);
  const currentUser = authAPI.getUser();
  const totalPropertySlots = Number(planInfo?.total_slots || 0);
  const usedPropertySlots = totalPropertySlots > 0
    ? Math.min(properties.length, totalPropertySlots)
    : properties.length;
  const remainingPropertySlots = totalPropertySlots > 0
    ? Math.max(totalPropertySlots - properties.length, 0)
    : 0;
  const hasAvailableSlots = totalPropertySlots > 0 && properties.length < totalPropertySlots;

  // useEffect(() => {
  //   const fetchPlanInfo = async () => {
  //     setIsPlanLoading(true);
  //     try {
  //       const [statsResult, subscriptionsResult, profileResult] = await Promise.all([
  //         sellerDashboardAPI.getStats(),
  //         sellerDashboardAPI.getSubscriptions(),
  //         sellerProfileAPI.get().catch(() => null),
  //       ]);
        

  //       const activeSubscriptions = subscriptionsResult?.success
  //         ? (subscriptionsResult.data?.subscriptions || []).filter((subscription) => subscription.status === 'active')
  //         : [];
  //       const statsSubscription = statsResult?.data?.subscription || null;
  //       const trialUser = profileResult?.success
  //         ? profileResult.data?.profile
  //         : currentUser;
  //       const derivedTotalSlots = activeSubscriptions.reduce(
  //         (total, subscription) => total + Number(subscription.properties_limit || 0),
  //         0
  //       ) + (hasActiveFreeTrial(statsSubscription, trialUser) ? FREE_LISTING_SLOTS : 0);
  //       const nearestExpiry = activeSubscriptions.length > 0
  //         ? activeSubscriptions
  //             .map((subscription) => subscription.ends_at)
  //             .filter(Boolean)
  //             .sort((a, b) => new Date(a) - new Date(b))[0] || null
  //         : statsResult?.data?.subscription?.nearest_expiry || null;

  //       setPlanInfo({
  //         total_slots: derivedTotalSlots,
  //         active_properties: properties.length,
  //         remaining_slots: Math.max(derivedTotalSlots - properties.length, 0),
  //         nearest_expiry: nearestExpiry,
  //       });
  //     } catch (error) {
  //       console.error('Failed to fetch subscription slot info:', error);
  //       setPlanInfo({ total_slots: 0, active_properties: 0, remaining_slots: 0, nearest_expiry: null });
  //     } finally {
  //       setIsPlanLoading(false);
  //     }
  //   };

  //   fetchPlanInfo();
  // }, [properties]);

// Update the fetchPlanInfo useEffect to correctly extract subscription data
useEffect(() => {
  const fetchPlanInfo = async () => {
    setIsPlanLoading(true);
    try {
      const [statsResult, subscriptionsResult, profileResult] = await Promise.all([
        sellerDashboardAPI.getStats(),
        sellerDashboardAPI.getSubscriptions(),
        sellerProfileAPI.get().catch(() => null),
      ]);
      
      let activeSubscriptions = [];
      let totalPaidSlots = 0;
      
      if (subscriptionsResult?.success && subscriptionsResult.data?.subscriptions) {
        // Log the raw subscription data to see what you're getting
        console.log('Raw subscriptions from API:', subscriptionsResult.data.subscriptions);
        
        // Filter active subscriptions that haven't expired
        activeSubscriptions = subscriptionsResult.data.subscriptions.filter(sub => {
          const isActive = sub.status === 'active';
          const hasValidEndDate = !sub.ends_at || new Date(sub.ends_at) > new Date();
          return isActive && hasValidEndDate;
        }).map(sub => ({
          id: sub.id,
          ends_at: sub.ends_at,
          starts_at: sub.starts_at,
          created_at: sub.created_at,
          properties_limit: sub.properties_limit || 1,
          status: sub.status
        }));
        
        console.log('Processed active subscriptions:', activeSubscriptions);
        
        totalPaidSlots = activeSubscriptions.reduce(
          (total, sub) => total + Number(sub.properties_limit || 0),
          0
        );
      }
      
      const statsSubscription = statsResult?.data?.subscription || null;
      const nearestExpiry =
        statsSubscription?.end_date || statsSubscription?.nearest_expiry || null;

      // Free plans may not appear in subscriptions history (no plan_id join),
      // but stats API still returns nearest_expiry from subscriptions.end_date.
      if (activeSubscriptions.length === 0 && nearestExpiry) {
        activeSubscriptions = [{
          id: statsSubscription?.id || 'active-plan',
          ends_at: nearestExpiry,
          starts_at: statsSubscription?.starts_at || null,
          created_at: statsSubscription?.created_at || null,
          properties_limit: Number(statsSubscription?.total_slots || FREE_LISTING_SLOTS),
          status: 'active',
        }];
      }

      const trialUser = profileResult?.success
        ? profileResult.data?.profile
        : currentUser;
      
      const trialCreatedAtRaw = trialUser?.created_at || trialUser?.createdAt || null;

      const hasFreeTrial = hasActiveFreeTrial(statsSubscription, trialUser);
      
      const derivedTotalSlots = totalPaidSlots + (hasFreeTrial ? FREE_LISTING_SLOTS : 0);
      
      setPlanInfo({
        total_slots: derivedTotalSlots,
        active_properties: properties.length,
        remaining_slots: Math.max(derivedTotalSlots - properties.length, 0),
        nearest_expiry: nearestExpiry,
        active_subscriptions: activeSubscriptions,
        paid_slots: totalPaidSlots,
        has_free_trial: hasFreeTrial,
        trial_created_at: trialCreatedAtRaw
      });
    } catch (error) {
      console.error('Failed to fetch subscription slot info:', error);
      setPlanInfo({ 
        total_slots: 0, 
        active_properties: 0, 
        remaining_slots: 0, 
        nearest_expiry: null,
        active_subscriptions: [],
        paid_slots: 0,
        has_free_trial: false,
        trial_created_at: null
      });
    } finally {
      setIsPlanLoading(false);
    }
  };

  fetchPlanInfo();
}, [properties]);
  // Filter and sort properties - memoized for performance
  const filteredProperties = useMemo(() => {
    return properties
      .filter(p => {
        const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
        const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             p.location.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'newest':
            return new Date(b.createdAt) - new Date(a.createdAt);
          case 'oldest':
            return new Date(a.createdAt) - new Date(b.createdAt);
          case 'price-high':
            return parseFloat(b.price) - parseFloat(a.price);
          case 'price-low':
            return parseFloat(a.price) - parseFloat(b.price);
          default:
            return 0;
        }
      });
  }, [properties, filterStatus, searchTerm, sortBy]);



  const openNew = useCallback(() => {
    if (!hasAvailableSlots) {
      alert('You have reached your current plan limit. Inactive properties still use their slots. Purchase another plan to add more properties.');
      navigate('/seller-dashboard/subscription');
      return;
    }
    setEditIndex(null);
    setShowForm(true);
  }, [hasAvailableSlots, navigate]);

  const openEdit = useCallback((idx) => {
    setEditIndex(idx);
    setShowForm(true);
  }, []);

  const handleDeleteClick = useCallback((id) => {
    setPropertyToDelete(id);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!propertyToDelete) return;
    try {
      await deleteProperty(propertyToDelete);
      setPropertyToDelete(null);
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Failed to delete property. Please try again.');
    }
  }, [propertyToDelete, deleteProperty]);

  const handleViewDetails = useCallback((propertyId) => {
    console.log('Navigating to property:', propertyId);
    // Navigate to seller dashboard details route to keep seller navbar - open in new tab
    const path = `/seller-dashboard/details/${propertyId}`;
    console.log('Navigation path:', path);
    window.open(path, '_blank', 'noopener,noreferrer');
  }, []);

  const formatPrice = useCallback((price) => {
    const num = parseFloat(price);
    if (num >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `₹${(num / 100000).toFixed(2)} Lac`;
    } else if (num >= 1000) {
      return `₹${(num / 1000).toFixed(1)}K`;
    }
    return `₹${num}`;
  }, []);

  const getPropertyIndex = useCallback((propertyId) => {
    return properties.findIndex(p => p.id === propertyId);
  }, [properties]);

  // const getPropertyPlanMeta = useCallback((property) => {
  //                           // property?.visibilityEndDate || planInfo?.nearest_expiry || null;
  //   const effectiveEndDate =  planInfo?.nearest_expiry || null;
  //   // if (!effectiveEndDate) {
  //   //   return {
  //   //     isExpired: false,
  //   //     daysLeft: null,
  //   //     label: 'Plan active'
  //   //   };
  //   // }

  //   const endDate = new Date(effectiveEndDate);
  //   const now = new Date();
  //   const diffMs = endDate.getTime() - now.getTime();
  //   const daysLeft = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
  //   // alert(endDate, now, diffMs, daysLeft);
  //   return {
  //     isExpired: daysLeft <= 0,
  //     daysLeft,
  //     label: daysLeft <= 0 ? 'Plan expired' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
  //   };
  // }, [planInfo?.nearest_expiry]);
// src/pages/SellerProperties.jsx - Updated getPropertyPlanMeta

// src/pages/SellerProperties.jsx - Fixed getPropertyPlanMeta for multiple subscriptions

// src/pages/SellerProperties.jsx - Complete working frontend solution

// Add this state at the top with other states (around line 60)
const [propertySubscriptionMap, setPropertySubscriptionMap] = useState(() => {
  // Try to load saved mapping from localStorage
  const saved = localStorage.getItem('propertySubscriptionMap');
  return saved ? JSON.parse(saved) : {};
});

// Save mapping to localStorage whenever it changes
useEffect(() => {
  localStorage.setItem('propertySubscriptionMap', JSON.stringify(propertySubscriptionMap));
}, [propertySubscriptionMap]);

// Replace your getPropertyPlanMeta with this corrected version
const getPropertyPlanMeta = useCallback((property) => {
  const paidSlots = getPaidSlotCount(planInfo);
  const sortedProperties = sortPropertiesByCreatedAt(properties);
  const propertyIndex = sortedProperties.findIndex((p) => p.id === property.id);
  const qualifiesForPaidSlot = propertyIndex >= 0 && propertyIndex < paidSlots;
  const freeListingPropertyId = planInfo?.has_free_trial
    ? getFreeListingPropertyId(properties, paidSlots)
    : null;
  const isFreeListing =
    freeListingPropertyId != null && property.id === freeListingPropertyId;

  const buildFreePlanMeta = () => {
    if (planInfo?.nearest_expiry && paidSlots === 0) {
      const daysLeft = getRemainingDays(planInfo.nearest_expiry);
      return {
        isExpired: daysLeft <= 0,
        daysLeft: daysLeft > 0 ? daysLeft : 0,
        label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
        isPaid: false,
        isFree: true,
      };
    }

    const createdAtRaw =
      currentUser?.created_at || currentUser?.createdAt || planInfo?.trial_created_at;
    const createdAt = toDate(createdAtRaw);
    if (!createdAt) {
      return { isExpired: true, daysLeft: 0, label: 'Expired', isPaid: false, isFree: false };
    }

    const trialEndsAt = new Date(createdAt.getTime() + (30 * DAY_IN_MS));
    const daysLeft = getRemainingDays(trialEndsAt);
    return {
      isExpired: daysLeft <= 0,
      daysLeft: daysLeft > 0 ? daysLeft : 0,
      label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
      isPaid: false,
      isFree: daysLeft > 0,
    };
  };

  // Paid listings are checked first so they never show the free-plan badge.
  if (qualifiesForPaidSlot && planInfo?.active_subscriptions?.length > 0) {
    const assignedSubscriptionId = propertySubscriptionMap[property.id];
    let subscription = assignedSubscriptionId
      ? planInfo.active_subscriptions.find((sub) => sub.id === assignedSubscriptionId)
      : null;

    if (!subscription) {
      const usedSubscriptionIds = Object.values(propertySubscriptionMap);
      const availableSubscriptions = planInfo.active_subscriptions.filter(
        (sub) => !usedSubscriptionIds.includes(sub.id)
      );
      const paidPropertyIndex = propertyIndex;
      if (paidPropertyIndex < availableSubscriptions.length) {
        subscription = availableSubscriptions[paidPropertyIndex];
        if (subscription) {
          setPropertySubscriptionMap((prev) => ({
            ...prev,
            [property.id]: subscription.id,
          }));
        }
      }
    }

    if (subscription?.ends_at) {
      const daysLeft = getRemainingDays(subscription.ends_at);
      return {
        isExpired: daysLeft <= 0,
        daysLeft: daysLeft > 0 ? daysLeft : 0,
        label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
        isPaid: true,
        isFree: false,
      };
    }

    if (planInfo?.nearest_expiry) {
      const daysLeft = getRemainingDays(planInfo.nearest_expiry);
      return {
        isExpired: daysLeft <= 0,
        daysLeft: daysLeft > 0 ? daysLeft : 0,
        label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
        isPaid: true,
        isFree: false,
      };
    }
  }

  if (isFreeListing) {
    return buildFreePlanMeta();
  }

  if (planInfo?.nearest_expiry && paidSlots === 0) {
    const daysLeft = getRemainingDays(planInfo.nearest_expiry);
    return {
      isExpired: daysLeft <= 0,
      daysLeft: daysLeft > 0 ? daysLeft : 0,
      label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
      isPaid: false,
      isFree: false,
    };
  }

  if (paidSlots === 0 && planInfo?.has_free_trial) {
    return buildFreePlanMeta();
  }

  return {
    isExpired: true,
    daysLeft: 0,
    label: 'Expired',
    isPaid: false,
    isFree: false,
  };
}, [
  planInfo?.active_subscriptions,
  planInfo?.nearest_expiry,
  planInfo?.has_free_trial,
  planInfo?.paid_slots,
  planInfo?.trial_created_at,
  currentUser,
  propertySubscriptionMap,
  properties
]);

// Add this useEffect to initialize subscription-property mapping
useEffect(() => {
  if (properties.length > 0 && planInfo?.active_subscriptions?.length > 0) {
    // Check if we need to initialize mapping
    const needsMapping = properties.some(p => !propertySubscriptionMap[p.id]);
    
    if (needsMapping) {
      // Sort subscriptions by start_date (or ends_at) - oldest first
      const sortedSubs = [...planInfo.active_subscriptions].sort((a, b) => {
        const dateA = new Date(a.starts_at || a.created_at);
        const dateB = new Date(b.starts_at || b.created_at);
        return dateA - dateB;
      });
      
      // Sort properties by creation date - oldest first
      const sortedProperties = [...properties].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      
      const newMappings = {};
      const paidSlots = getPaidSlotCount(planInfo);
      const paidProperties = sortedProperties.slice(0, paidSlots);

      paidProperties.forEach((property, index) => {
        // Assign subscriptions in order, cycling if more properties than subscriptions
        const subIndex = index % sortedSubs.length;
        if (sortedSubs[subIndex]) {
          newMappings[property.id] = sortedSubs[subIndex].id;
          console.log(`Mapping: Property ${property.id} (created ${property.createdAt}) -> Subscription ${sortedSubs[subIndex].id} (ends ${sortedSubs[subIndex].ends_at})`);
        }
      });
      
      setPropertySubscriptionMap(newMappings);
    }
  }
}, [properties, planInfo?.active_subscriptions]);

// Add debug logging to see what subscriptions you're getting
useEffect(() => {
  console.log('=== Debug Info ===');
  console.log('Active Subscriptions:', planInfo?.active_subscriptions?.map(sub => ({
    id: sub.id,
    ends_at: sub.ends_at,
    starts_at: sub.starts_at,
    properties_limit: sub.properties_limit
  })));
  console.log('Properties:', properties.map(p => ({ id: p.id, createdAt: p.createdAt })));
  console.log('Property-Subscription Map:', propertySubscriptionMap);
  const createdAtRaw = currentUser?.created_at || currentUser?.createdAt || planInfo?.trial_created_at;
  const createdAt = toDate(createdAtRaw);
  const trialEndsAt = createdAt ? new Date(createdAt.getTime() + (30 * DAY_IN_MS)) : null;
  console.log('Trial Debug:', {
    createdAtRaw,
    createdAt: createdAt ? createdAt.toISOString() : null,
    trialEndsAt: trialEndsAt ? trialEndsAt.toISOString() : null,
    daysLeft: trialEndsAt ? getRemainingDays(trialEndsAt) : null
  });
}, [planInfo?.active_subscriptions, properties, propertySubscriptionMap]);

// Add this useEffect t
// o initialize property-subscription mapping when properties load
useEffect(() => {
  if (properties.length > 0 && planInfo?.active_subscriptions?.length > 0) {
    // Only initialize if we haven't mapped these properties yet
    const needsMapping = properties.some(p => !propertySubscriptionMap[p.id]);
    
    if (needsMapping) {
      // Sort subscriptions by creation date (oldest first)
      const sortedSubs = [...planInfo.active_subscriptions].sort(
        (a, b) => new Date(a.created_at || a.starts_at) - new Date(b.created_at || b.starts_at)
      );
      
      // Sort properties by creation date (oldest first)
      const sortedProperties = [...properties].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      
      const newMappings = {};
      let subscriptionIndex = 0;
      let currentSlotCount = 0;
      
      sortedProperties.forEach((property) => {
        // If we've exhausted all subscriptions, stop
        if (subscriptionIndex >= sortedSubs.length) {
          return;
        }
        
        // Check if current subscription has reached its limit
        if (currentSlotCount >= (sortedSubs[subscriptionIndex]?.properties_limit || 1)) {
          subscriptionIndex++;
          currentSlotCount = 0;
        }
        
        // Assign subscription if available
        if (subscriptionIndex < sortedSubs.length) {
          newMappings[property.id] = sortedSubs[subscriptionIndex].id;
          currentSlotCount++;
          console.log(`Assigned property ${property.id} to subscription ${sortedSubs[subscriptionIndex].id} (ends ${sortedSubs[subscriptionIndex].ends_at})`);
        }
      });
      
      if (Object.keys(newMappings).length > 0) {
        setPropertySubscriptionMap(newMappings);
      }
    }
  }
}, [properties, planInfo?.active_subscriptions]);

// Add this effect to check for expired subscriptions and clear them
useEffect(() => {
  if (planInfo?.active_subscriptions?.length > 0 && Object.keys(propertySubscriptionMap).length > 0) {
    const updatedMappings = { ...propertySubscriptionMap };
    let hasChanges = false;
    
    // Check each mapped property's subscription
    Object.entries(propertySubscriptionMap).forEach(([propertyId, subscriptionId]) => {
      const subscription = planInfo.active_subscriptions.find(sub => sub.id === subscriptionId);
      if (!subscription || new Date(subscription.ends_at) <= new Date()) {
        // Subscription expired, remove the mapping
        delete updatedMappings[propertyId];
        hasChanges = true;
        console.log(`Removed expired subscription ${subscriptionId} from property ${propertyId}`);
      }
    });
    
    if (hasChanges) {
      setPropertySubscriptionMap(updatedMappings);
    }
  }
}, [planInfo?.active_subscriptions, propertySubscriptionMap]);


  // const handleRenewProperty = useCallback(async (propertyId) => {
  //   navigate('/seller-dashboard/checkout', {
  //     state: {
  //       planId: 'basic_listing',
  //       renewPropertyId: propertyId,
  //       returnTo: '/seller-dashboard/properties'
  //     }
  //   });
  // }, [navigate]);

// Update handleRenewProperty to properly handle renewal
const handleRenewProperty = useCallback(async (propertyId) => {
  // Store which property is being renewed
  sessionStorage.setItem('renewingPropertyId', propertyId);
  
  // Remove the old mapping for this property
  setPropertySubscriptionMap(prev => {
    const newMap = { ...prev };
    delete newMap[propertyId];
    return newMap;
  });
  
  navigate('/seller-dashboard/checkout', {
    state: {
      planId: 'basic_listing',
      renewPropertyId: propertyId,
      returnTo: '/seller-dashboard/properties'
    }
  });
}, [navigate]);



// Update post-payment effect
useEffect(() => {
  const checkPaymentCompletion = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const renewedPropertyId = sessionStorage.getItem('renewingPropertyId');
    
    if (paymentSuccess === 'true' && renewedPropertyId) {
      sessionStorage.removeItem('renewingPropertyId');
      
      // Refresh properties and subscriptions
      await refreshProperties();
      
      // Wait a bit for the subscription to be available
      setTimeout(async () => {
        // Re-fetch subscriptions
        const subscriptionsResult = await sellerDashboardAPI.getSubscriptions();
        if (subscriptionsResult?.success && subscriptionsResult.data?.subscriptions) {
          const newActiveSubscriptions = subscriptionsResult.data.subscriptions.filter(sub => 
            sub.status === 'active' && 
            (!sub.ends_at || new Date(sub.ends_at) > new Date())
          );
          
          // Find the newest subscription (the one just purchased)
          const newestSubscription = newActiveSubscriptions.sort(
            (a, b) => new Date(b.created_at || b.starts_at) - new Date(a.created_at || a.starts_at)
          )[0];
          
          if (newestSubscription) {
            // Assign the new subscription to the renewed property
            setPropertySubscriptionMap(prev => ({
              ...prev,
              [renewedPropertyId]: newestSubscription.id
            }));
            console.log(`Assigned new subscription ${newestSubscription.id} to property ${renewedPropertyId}`);
          }
        }
      }, 2000); // Wait 2 seconds for DB to update
      
      // Remove query param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };
  
  checkPaymentCompletion();
}, [refreshProperties]);


  const isPropertyVisibleToBuyers = (property) =>
    property?.isActive === true || property?.isActive === 1 || property?.isActive === '1';

  const handleToggleVisibility = useCallback(async (property) => {
    try {
      setTogglingPropertyId(property.id);
      const currentlyVisible = isPropertyVisibleToBuyers(property);
      const nextVisible = !currentlyVisible;

      const response = await sellerPropertiesAPI.toggleVisibility(property.id, nextVisible);
      if (response.success !== true) {
        throw new Error(response.message || 'Failed to update property visibility');
      }

      const serverActive = response.data?.property?.is_active;
      const resolvedActive =
        serverActive !== undefined && serverActive !== null
          ? serverActive === true || serverActive === 1 || serverActive === '1'
          : nextVisible;

      setProperties((prev) =>
        prev.map((p) => (p.id === property.id ? { ...p, isActive: resolvedActive } : p))
      );

      await refreshProperties();
    } catch (error) {
      console.error('Toggle property visibility failed:', error);
      alert(error.message || 'Failed to update property visibility. Please try again.');
    } finally {
      setTogglingPropertyId(null);
    }
  }, [refreshProperties, setProperties]);

  const handleToggleClick = useCallback((property) => {
    setPropertyToToggle(property);
    setVisibilityModalOpen(true);
  }, []);

  const handleVisibilityConfirm = useCallback(async () => {
    if (!propertyToToggle) return;
    await handleToggleVisibility(propertyToToggle);
    setVisibilityModalOpen(false);
    setPropertyToToggle(null);
  }, [propertyToToggle, handleToggleVisibility]);

  return (
    <div className="seller-properties">
      {/* Header */}
      <div className="seller-props-header">
        <div className="seller-props-header-content">
          <div>
            <h1>My Properties</h1>
            <p className="seller-props-subtitle">
              {properties.length} propert{properties.length === 1 ? 'y' : 'ies'} listed
            </p>
            <p className="seller-props-plan-status">
              {isPlanLoading
                ? 'Checking plan slots...'
                : hasAvailableSlots
                  ? `${remainingPropertySlots} property slot${remainingPropertySlots === 1 ? '' : 's'} available`
                  : totalPropertySlots > 0
                    ? `No property slot available. ${usedPropertySlots}/${totalPropertySlots} slots are already used.`
                    : 'No property slot available. Purchase a plan to add more.'
              }
            </p>
          </div>
          <button 
            className="seller-props-add-btn" 
            onClick={openNew}
            disabled={isPlanLoading}
            title={hasAvailableSlots ? "Add a new property" : "No property slot available. View plans"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Property
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="seller-props-toolbar">
        <div className="seller-props-search-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="seller-props-toolbar-actions">
          <div className="seller-props-filter-group">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Rent</option>
            </select>

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price-high">Price: High to Low</option>
              <option value="price-low">Price: Low to High</option>
            </select>
          </div>

          <div className="seller-props-view-toggle">
            <button 
              className={`seller-props-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
                <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            <button 
              className={`seller-props-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Properties Grid/List */}
      {isPlanLoading ? (
        <div className="seller-props-empty-state">
          <div className="seller-props-empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M12 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M22 12h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M12 22v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M2 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M19.07 4.93l-2.83 2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M19.07 19.07l-2.83-2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M4.93 4.93l2.83 2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M4.93 19.07l2.83-2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <h3>Checking properties...</h3>
          <p>Preparing your available listing slots.</p>
        </div>
      ) : filteredProperties.length === 0 ? (
        <div className="seller-props-empty-state">
          <div className="seller-props-empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          <h3>No Properties Found</h3>
          <p>
            {searchTerm || filterStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'Start by adding your first property listing'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <button className="seller-props-empty-action-btn" onClick={openNew}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Your First Property
            </button>
          )}
        </div>
      ) : (
        <div className={`seller-props-container ${viewMode}`}>
          {filteredProperties.map((property, index) => {
            const planMeta = getPropertyPlanMeta(property);
            const isActive = !!property.isActive;

            return (
            <div 
              key={property.id} 
              className={`seller-props-card ${viewMode}`}
              style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }}
              onClick={() => handleViewDetails(property.id)}
            >
              <div className="seller-props-image">
                <img 
                  src={(() => {
                    // Prioritize display_image, then first image, then cover_image
                    const imageUrl = property.display_image 
                      || property.images?.[0] 
                      || property.cover_image;
                    if (!imageUrl) return 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';
                    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                      return imageUrl;
                    }
                    if (imageUrl.startsWith('/')) {
                      return `${API_BASE_URL.replace('/api', '')}${imageUrl}`;
                    }
                    return `${API_BASE_URL.replace('/api', '')}/uploads/${imageUrl}`;
                  })()} 
                  alt={property.title} 
                  onError={(e) => {
                    console.error('Image load failed for property:', property.id, 'URL:', e.target.src);
                    e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';
                  }}
                />
                <div className="seller-props-image-overlay">
                  <button 
                    className="seller-props-overlay-btn" 
                    onClick={(e) => {
                      e.stopPropagation();
                      openEdit(getPropertyIndex(property.id));
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                  <button 
                    className="seller-props-overlay-btn delete" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(property.id);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
                <span className={`seller-props-badge ${property.status}`}>
                  {property.status === 'sale' ? 'For Sale' : 'For Rent'}
                </span>
                
                <span className={`seller-props-plan-days ${planMeta.isExpired ? 'expired' : 'active'}`}>
                  {planMeta.daysLeft != null
                    ? formatDaysLeftLabel(planMeta.daysLeft, planMeta.isExpired)
                    : planMeta.label}
                </span>
                {property.featured && (
                  <span className="seller-props-featured-badge">Featured</span>
                )}
              </div>

              <div className="seller-props-content">
                <div className="seller-props-title-row">
                  <h3 className="seller-props-title">{property.title}</h3>
                  <span className={`seller-props-live-status ${isActive ? 'on' : 'off'}`}>
                    {isActive ? 'On' : 'Off'}
                  </span>
                </div>
                {planMeta.isFree && !planMeta.isPaid && !planMeta.isExpired && (
                  <p className="seller-props-free-plan">{FREE_PLAN_LABEL}</p>
                )}
                <p className="seller-props-location">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {property.location}
                </p>

                <div className="seller-props-features">
                  <span className="seller-props-feature">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M3 22V8l9-6 9 6v14H3z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M9 22v-6h6v6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {property.bedrooms} Beds
                  </span>
                  <span className="seller-props-feature">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M4 12h16v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M6 12V5a2 2 0 012-2h2v9" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {property.bathrooms} Baths
                  </span>
                  <span className="seller-props-feature">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {property.area} sqft
                  </span>
                </div>

                <div className="seller-props-stats">
                  <div className="seller-props-stat-item" title={`${property.views || 0} people showed interest in this property`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{property.views || 0} {property.views === 1 ? 'person' : 'people'} interested</span>
                  </div>
                  <div className="seller-props-stat-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{property.inquiries || 0} inquiries</span>
                  </div>
                </div>

                <div className="seller-props-footer">
                  <div className="seller-props-footer-top">
                    <div className="seller-props-price-section">
                      <span className="seller-props-price">{formatPrice(property.price)}</span>
                      {property.status === 'rent' && <span className="seller-props-per-month">/month</span>}
                    </div>
                  </div>
                  <div className="seller-props-action-btns">
                    <div className="seller-props-action-group seller-props-action-group-left">
                      <button 
                        className="seller-props-edit-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(getPropertyIndex(property.id));
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Edit
                      </button>
                      <button 
                        className="seller-props-view-details-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(property.id);
                        }}
                        title="View Details"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        View
                      </button>
                    </div>
                    <div className="seller-props-action-group seller-props-action-group-right">
                      <button
                        className={`seller-props-toggle-btn ${isActive ? 'on' : 'off'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleClick(property);
                        }}
                        disabled={togglingPropertyId === property.id}
                        title={
                          isActive
                            ? 'Hide this property from buyer search and details'
                            : 'Show this property to buyers again'
                        }
                      >
                        {togglingPropertyId === property.id
                          ? 'Updating...'
                          : isActive
                          ? 'Hide'
                          : 'Show'}
                      </button>
                      <button 
                        className="seller-props-delete-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(property.id);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Delete
                      </button>
                      {planMeta.isExpired && (
                        <button
                          className="seller-props-renew-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRenewProperty(property.id);
                          }}
                        >
                          Renew
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            );
          })}

          {/* Add Property Card */}
          {viewMode === 'grid' && (
            <div className="seller-props-card seller-props-add-card" onClick={openNew}>
              <div className="seller-props-add-card-content">
                <div className="seller-props-add-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>Add New Property</h3>
                <p>List a new property for sale or rent</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <AddPropertyPopup
          onClose={() => setShowForm(false)}
          editIndex={editIndex}
          initialData={editIndex !== null ? properties[editIndex] : null}
        />
      )}



      {/* Delete Confirmation Modal */}
      <DeletePropertyModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPropertyToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {visibilityModalOpen && propertyToToggle && (
        <div className="seller-props-visibility-modal-backdrop" onClick={() => {
          setVisibilityModalOpen(false);
          setPropertyToToggle(null);
        }}>
          <div
            className="seller-props-visibility-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{propertyToToggle.isActive ? 'Hide Property?' : 'Show Property?'}</h3>
            <p>
              {propertyToToggle.isActive
                ? 'This property will be hidden from buyers until you turn it on again.'
                : 'This property will be visible to buyers after you turn it on.'}
            </p>
            <div className="seller-props-visibility-modal-actions">
              <button
                type="button"
                className="seller-props-visibility-cancel"
                onClick={() => {
                  setVisibilityModalOpen(false);
                  setPropertyToToggle(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="seller-props-visibility-confirm"
                onClick={handleVisibilityConfirm}
                disabled={togglingPropertyId === propertyToToggle.id}
              >
                {togglingPropertyId === propertyToToggle.id
                  ? 'Updating...'
                  : propertyToToggle.isActive
                  ? 'Hide Property'
                  : 'Show Property'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerProperties;
